import { useState, useEffect, useRef } from 'react'
import { Input } from '@/components/ui/input'

interface EditableCellProps {
  value: string | null
  onSave: (value: string) => void
  isEditing: boolean
  onStartEdit: () => void
  onCancelEdit: () => void
  inputType?: 'text' | 'number'
  onTab?: (shiftKey: boolean) => boolean | void
  onEnter?: (shiftKey: boolean) => void
}

export function EditableCell({
  value,
  onSave,
  isEditing,
  onStartEdit,
  onCancelEdit,
  inputType = 'text',
  onTab,
  onEnter,
}: EditableCellProps) {
  const [editValue, setEditValue] = useState(value || '')
  const inputRef = useRef<HTMLInputElement>(null)
  const committedRef = useRef(false)

  // Update editValue when value prop changes (e.g., after successful save)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value || '')
    }
  }, [value, isEditing])

  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      committedRef.current = false
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = () => {
    if (committedRef.current) return
    committedRef.current = true
    if (editValue !== (value || '')) {
      onSave(editValue)
    } else {
      onCancelEdit()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()
    if (e.nativeEvent.isComposing) return
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
      onEnter?.(e.shiftKey)
    } else if (e.key === 'Tab') {
      handleSave()
      if (onTab?.(e.shiftKey) !== false) e.preventDefault()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      committedRef.current = true
      setEditValue(value || '')
      onCancelEdit()
    }
  }

  const handleBlur = () => {
    handleSave()
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        type={inputType}
        step={inputType === 'number' ? 'any' : undefined}
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className="h-8 border-0 border-b-2 border-blue-500 rounded-none px-1 focus-visible:ring-0 focus-visible:ring-offset-0"
      />
    )
  }

  return (
    <div
      onDoubleClick={onStartEdit}
      className="cursor-pointer min-h-[2rem] flex items-center px-1 w-full overflow-hidden"
    >
      <span className="truncate">{value || '-'}</span>
    </div>
  )
}
